/**
 * 매칭리스트 문자발송 — 도메인 상수 + 순수 유틸리티
 *
 * MatchList.js 와 MatchList/SendModal.js 가 공유.
 * closure / 인스턴스 상태 의존이 없는 함수만 모음.
 */

// ============================================================
//  도메인 상수
// ============================================================
export const DT_LANG = {
    info:        '총 _TOTAL_명',
    infoEmpty:   '0명',
    zeroRecords: '데이터가 없습니다',
    emptyTable:  '데이터가 없습니다',
};

export const EQUIPMENT_TYPES = [
    { key: 'sky',       label: '스카이' },
    { key: 'ladder',    label: '사다리' },
    { key: 'crane',     label: '크레인' },
    { key: 'excavator', label: '굴삭기' },
    { key: 'forklift',  label: '지게차' },
    { key: 'lift',      label: '리프트' },
];

// 마지막 Infinity = 개방 버킷("100km ~" = 100km 초과, 101km 이상). 직전 100 = 51~100.
export const DISTANCE_BUCKETS = {
    sky:       [3, 5, 10, 15, 20, 30, 50, 100, Infinity],
    ladder:    [3, 5, 10, 15, 20, 30, 50, 100, Infinity],
    crane:     [3, 5, 10, 15, 20, 30, 50, 100, Infinity],
    excavator: [3, 5, 10, 15, 20, 30, 50, 100, Infinity],
    forklift:  [3, 5, 10, 15, 20, 30, 50, 100, Infinity],
    lift:      [3, 5, 10, 15, 20, 30, 50, 100, Infinity],
};

// 접속 선택 — 오늘 접속(접속) / 오늘 미접속(미접속) 2분류. lastAccess 의 경과일로 판정.
export const ACCESS_GROUPS = [
    { value: 'online',  label: '접속' },
    { value: 'offline', label: '미접속' },
];

// 경과일 → 접속 그룹. 0일(오늘/24h 이내)=접속, 그 외(과거·파싱실패)=미접속
export const accessGroupFor = (days) => (days === 0 ? 'online' : 'offline');

// 유료/무료 선택 — 유료회원(sosok 012001 지사)만 / 무료회원만 보기
export const PAID_GROUPS = [
    { value: 'paid', label: '유료' },
    { value: 'free', label: '무료' },
];

export const paidGroupOf = (m) => (m && m.isPaid ? 'paid' : 'free');

// 미접속 = 최근접속일에서 N일 이내. 클릭하면 daysSince(lastAccess) <= value 인 회원이 선택됨
export const INACTIVE_PERIODS = [
    { value: 5,  label: '5일' },
    { value: 7,  label: '7일' },
    { value: 10, label: '10일' },
    { value: 15, label: '15일' },
    { value: 30, label: '1개월' },
    { value: 60, label: '2개월' },
    { value: 90, label: '3개월' },
];

// 외부배차 최근오더일 버킷 — 작업일에서 N일 이내. 클릭하면 해당 버킷의 외부회원이 선택됨
export const WORK_PERIODS = [
    { value: 30,   label: '1개월' },
    { value: 90,   label: '3개월' },
    { value: 180,  label: '6개월' },
    { value: 365,  label: '1년' },
    { value: 1095, label: '3년' },
];

// 차수 색상 팔레트 (파스텔톤 24색) — match_lvl % 24 로 매핑
export const LEVEL_COLORS = [
    '#FFDDD1', '#FFE8D1', '#FFF4D1', '#FFFFD1',
    '#F4FFD1', '#E8FFD1', '#DDFFD1', '#D1FFD1',
    '#D1FFDD', '#D1FFE8', '#D1FFF4', '#D1FFFF',
    '#D1F4FF', '#D1E8FF', '#D1DDFF', '#D1D1FF',
    '#DDD1FF', '#E8D1FF', '#F4D1FF', '#FFD1FF',
    '#FFD1F4', '#FFD1E8', '#FFD1DD', '#FFD1D1',
];

// ============================================================
//  순수 유틸리티
// ============================================================
export const initDataTable = ($tbl, opts = {}) => {
    const $ = jQuery;
    if (!$tbl || !$tbl.length || !$.fn.DataTable) return null;
    if ($.fn.DataTable.isDataTable($tbl)) $tbl.DataTable().clear().destroy();
    $tbl.addClass('display compact');
    return $tbl.DataTable(Object.assign({
        paging:       false,
        lengthChange: false,
        searching:    false,
        language:     DT_LANG,
        order:        [],
        destroy:      true,
    }, opts));
};

export const haversineKm = (lat1, lng1, lat2, lng2) => {
    const R = 6371;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a = Math.sin(dLat / 2) ** 2
            + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180)
            * Math.sin(dLng / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

export const parseFlexibleDate = (s) => {
    if (!s) return null;
    if (s === '어제')   { const d = new Date(); d.setDate(d.getDate() - 1);    return d; }
    if (s === '일주일') { const d = new Date(); d.setDate(d.getDate() - 7);    return d; }
    if (s === '한달')   { const d = new Date(); d.setMonth(d.getMonth() - 1);  return d; }
    if (/^\d{2}:\d{2}:\d{2}$/.test(s)) return new Date();
    const d = new Date(s.replace(' ', 'T'));
    return isNaN(d.getTime()) ? null : d;
};

// 파싱 실패는 Infinity 반환 — 미접속 N일 이내 필터에서 제외됨
export const daysSince = (s) => {
    const d = parseFlexibleDate(s);
    if (!d) return Infinity;
    return Math.floor((Date.now() - d.getTime()) / 86400000);
};

// DataTables 정렬용 timestamp — 파싱 실패는 0(가장 오래).
export const lastAccessSortKey = (s) => {
    const d = parseFlexibleDate(s);
    return d ? d.getTime() : 0;
};

// 값이 없으면 줄 통째로 빼는 선택 변수 (예: 현장번호 없으면 '현장번호 : ' 줄 미노출)
const OPTIONAL_PLACEHOLDERS = ['현장번호'];
// 값이 없어도 정상인 변수 — 미리보기에서 '{옵션}' 글자 대신 빈칸으로 보여준다 (실제 발송문과 같게)
const BLANKABLE_PLACEHOLDERS = ['옵션', '수수료'];

export const expandPlaceholders = (template, vars) => {
    return template.split('\n').map((line) => {
        // 이 줄에 선택 변수가 있는데 값이 비어있으면 줄 자체를 제거
        const drop = OPTIONAL_PLACEHOLDERS.some(k => line.includes(`{${k}}`) && !(vars[k] ?? ''));
        if (drop) return null;
        let out = line;
        Object.entries(vars).forEach(([key, val]) => {
            out = out.replaceAll(`{${key}}`, val);
        });
        return out;
    }).filter(l => l !== null).join('\n');
};

export const splitTemplateForHighlight = (template, vars) => {
    const keys = Object.keys(vars).join('|');
    const pattern = new RegExp(`\\{(${keys})\\}`, 'g');
    const parts = [];
    let last = 0;
    let match;
    while ((match = pattern.exec(template)) !== null) {
        if (match.index > last) parts.push({ text: template.slice(last, match.index), bold: false });
        parts.push({ text: vars[match[1]] || (BLANKABLE_PLACEHOLDERS.includes(match[1]) ? '' : match[0]), bold: true });
        last = match.index + match[0].length;
    }
    if (last < template.length) parts.push({ text: template.slice(last), bold: false });
    return parts;
};

export const distanceBucketFor = (distance, buckets) => {
    for (let i = 0; i < buckets.length; i++) {
        const lower  = i === 0 ? 0 : buckets[i - 1];
        const isLast = i === buckets.length - 1;   // 마지막 개방 버킷(Infinity)은 상한 없음 = lower 초과
        if (distance > lower && (isLast || distance <= buckets[i])) return buckets[i];
    }
    return null;
};

// 거리 라벨 — 유한 버킷은 "100km", 마지막 개방(Infinity) 버킷은 직전 경계로 "100km ~"(=초과). 필터·설정 공통.
export const kmLabel = (km, buckets) => {
    if (Number.isFinite(km)) return `${km}km`;
    return `${buckets[buckets.length - 2]}km ~`;
};

export const periodsUpTo = (max) =>
    INACTIVE_PERIODS.filter(p => p.value <= max).map(p => p.value);

export const maxValue = (arr) => arr.length ? Math.max(...arr) : 0;

// 수발주제한 (g5_member_relation) — 일괄 자동선택에서 제외하기 위한 판정
export const isRestricted = (m) => {
    const r = m && m.restrictions;
    return !!(r && (r.takeBlocked || r.giveBlocked));
};

export const byteLength = (s) => {
    let total = 0;
    for (let i = 0; i < s.length; i++) total += s.charCodeAt(i) > 127 ? 2 : 1;
    return total;
};

export const smsTypeOf = (s) => byteLength(s) > 90 ? 'LMS' : 'SMS';

// 발송내역/내부테이블/펼치기 등 어디서나 동일한 RCS/SMS/LMS 배지 마크업을 사용
export const msgTypeBadge = (type) => {
    if (!type) return '';
    const cls = type === 'RCS' ? 'tb-r' : (type === 'LMS' ? 'tb-l' : 'tb-s');
    return `<span class="tb2 ${cls}">${type}</span>`;
};

export const smsBadge = (s) => {
    const type = smsTypeOf(s);
    const bytes = byteLength(s);
    const color = type === 'LMS' ? '#DC2626' : '#15803D';
    const suffix = type === 'LMS' ? ' (초과)' : '';
    return `<span style="font-size:12px;font-weight:700;color:${color}">${type} ${bytes}/90byte${suffix}</span>`;
};

// 현장주소 → 동/읍/면 까지만. 광역시/특별시/특별자치시는 앞 2자.
//  예) "대전광역시 유성구 봉명동 123"     → "대전 유성구 봉명동"
//      "경기도 성남시 분당구 정자동 ..."   → "경기도 성남시 분당구 정자동"
//      "경기도 양평군 양평읍 ..."          → "경기도 양평군 양평읍"
//  지번/행정동 표기가 시·구 뒤 3~4번째에 오므로 위치를 고정하지 않고 동/읍/면 토큰을 찾는다.
//  동/읍/면 토큰이 없으면(도로명 주소 등) 시·구 까지만.
export const shortAddress = (a) => {
    const s = String(a ?? '').trim();
    if (!s) return '';
    const parts = s.split(/\s+/);
    if (parts.length < 2) return s;
    if (/(광역시|특별시|특별자치시)$/.test(parts[0])) parts[0] = parts[0].slice(0, 2);
    const dongIdx = parts.findIndex(p => /[동읍면]$/.test(p));
    const end = dongIdx >= 0 ? dongIdx : 1; // 동/읍/면 없으면 시·구(index 1) 까지
    return parts.slice(0, end + 1).join(' ');
};

export const shortDateTime = (d) => {
    const m = d.match(/\d{4}-(\d{2})-(\d{2}\s+\d{2}:\d{2})/);
    return m ? `${m[1]}/${m[2]}` : d;
};

export const distanceColor = (km) =>
    km <=  10 ? '#15803D' :
    km <=  30 ? '#1D4ED8' :
    km <=  60 ? '#B45309' : '#909090';

export const escapeHtml = (s) => String(s ?? '').replace(/</g, '&lt;');